import { useEffect, useCallback, useRef, useState } from 'react'
import { Link, useHistory, useParams, matchPath } from 'react-router-dom'
import { tabbable } from 'tabbable'
import {
    Combobox,
    ComboboxInput,
    ComboboxPopover,
    ComboboxList,
    ComboboxOption
} from '@reach/combobox'

import DashboardHubLanding from './DashboardHubLanding/DashboardHubLanding'
import DashboardHubTopicsCard from './DashboardHubTopicsCard/DashboardHubTopicsCard'

import ArticleCard from '../../../components/ArticleCard/ArticleCard'
import { ARTICLE_TAG_ICONS } from '../../../components/ArticleIconTags/ArticleIconTags'
import Form from '../../../components/Form/Form'
import RichText from '../../../components/RichText/RichText'
import LoadingIndicator from '../../../components/LoadingIndicator/LoadingIndicator'

import useEventListener from '../../../hooks/useEventListener'
import useWindowSize from '../../../hooks/useWindowSize'

import { getCssVar } from '../../../utils/css'
import { getQueryParams } from '../../../utils/location'
import { graphqlClient, gql } from '../../../utils/gql'
import { scrollTo } from '../../../utils/dom'

export default function DashboardHub (props = {
    heading: null,
    richText: null,
    searchPath: null
}) {
    let { heading } = props

    if (!heading)
        heading = 'Hub'

    const history = useHistory()

    const { location } = history

    const isLanding = !(Array.isArray(props.searchPath)
        && props.searchPath
            .some(str => matchPath(location.pathname, {
                path: str,
                exact: true,
                strict: true
            })))

    const params = useParams()

    const form = useRef()

    const queryParams = getQueryParams(location.search)

    const [searchValue, setSearchValue] = useState(queryParams.q)

    const [{ articles, count }, setArticles] = useState({
        articles: ((window.history.state || {}).articles || []) || null,
        count: ((window.history.state || {}).count || []) || null
    })

    const [tags, setTags] = useState((window.history.state || {}).tags || null)

    const [topics, setTopics]
        = useState(((window.history.state || {}).topics || []) || null)

    const [mediaTypes, setMediaTypes]
        = useState(((window.history.state || {}).media || []) || null)

    const [sources, setSources] = useState((window.history.state || {})
        .sources || null)

    const [filtersExpanded, setFiltersExpanded] = useState(false)

    const [topicFiltersExpanded, setTopicFiltersExpanded] = useState(true)

    const [mediaFiltersExpanded, setMediaFiltersExpanded] = useState(true)

    const [durationFiltersExpanded, setDurationFiltersExpanded] = useState(true)

    const [sourceFiltersExpanded, setSourceFiltersExpanded] = useState(true)

    const [{ page, queryString }, setParams] = useState({
        page: null,
        queryString: null
    })

    const [source, setSource] = useState({
        id: null,
        slug: null,
        title: null,
        description: null,
        url: null
    })

    const inputSearch = useRef()

    const combobox = useRef()

    const size = useWindowSize()

    const [comboboxExpanded, setComboboxExpanded] = useState()

    const isComboboxExpandable = size.width < 1024

    useEffect(() => {
        if (comboboxExpanded
                && window.innerWidth >= 1024)
            setComboboxExpanded(false)
    }, [size])

    // Tab trap
    useEventListener('keydown', useCallback((e) => {
        if (!(comboboxExpanded
                && window.innerWidth < 1024))
            return

        // On Escape
        if (e.key.match(/escape/i))
            setComboboxExpanded(false)

        // Trap Tab keypresses
        if (!e.key.match(/tab/i)
                || !combobox.current)
            return

        let activeEl

        const els = tabbable(combobox.current) || []

        if (e.shiftKey
                && els.indexOf(e.target) === 0)
            activeEl = els[els.length - 1]

        if (!e.shiftKey
                && els.indexOf(e.target) === els.length - 1)
            activeEl = els[0]

        if (!activeEl)
            return

        e.preventDefault()
        e.stopPropagation()

        activeEl.focus()
    }, [comboboxExpanded]))

    const topicIcons = Object.keys(ARTICLE_TAG_ICONS)
        .filter(str => !str.match(/(article|audio|video)/))
        .reduce((acc, curr) => Object.assign(acc, {
            [curr]: ARTICLE_TAG_ICONS[curr]
        }), {})

    const limit = 18

    const offset = (page - 1) * limit

    if (typeof queryParams.topic === 'string')
        queryParams.topic = [queryParams.topic]

    if (typeof queryParams.media === 'string')
        queryParams.media = [queryParams.media]

    if (typeof queryParams.source === 'string')
        queryParams.source = [queryParams.source]

    const isQuery = (queryParams.q || '').length > 1

    const durationFilters = [{
        label: 'All',
        value: 'all'
    }, {
        label: 'Quick (less than 5m)',
        value: 'quick',
        where: gql`duration: { _lt: 300 }`
    }, {
        label: 'Casual (5-10m)',
        value: 'casual',
        where: gql`_and: [
            { duration: { _gte: 300 } },
            { duration: { _lt: 600 } }
        ]`
    }, {
        label: 'Long (10m+)',
        value: 'long',
        where: gql`duration: { _gte: 600 }`
    }]

    const durationFilterActive = durationFilters
        .filter(obj => obj.where
            && queryParams.duration === obj.value)[0]
        || durationFilters[0]

    const isQueryParamsQ = Boolean(queryParams.q)

    const isQueryParamsFilters = Boolean(queryParams.topic
        || queryParams.media
        || queryParams.duration
        || queryParams.source)

    const isQueryParams = isQueryParamsQ
        || isQueryParamsFilters

    const getQueryParamsCount = () => {
        let count = 0

        if (queryParams.duration !== 'all')
            count += 1

        if ((queryParams.topic || [])
            .some(str => str.indexOf((topics || [])
                .map(obj => obj.slug)) >= 0))
            count += 1

        if ((queryParams.source || [])
            .some(str => str.indexOf((sources || [])
                .map(obj => obj.slug)) >= 0))
            count += 1

        return count
    }

    useEffect(async () => {
        if (!page
                || isLanding)
            return

        if (isQueryParams)
            Object.keys(queryParams)
                .filter(str => str !== 'q')
                .forEach(str => {
                    const params = typeof queryParams[str] === 'string'
                        ? [queryParams[str]]
                        : queryParams[str]

                    params
                        .filter(_str => _str)
                        .map(_str => document
                            .querySelector(`[name=${str}][value=${_str}]`))
                        .filter(el => el && !el.checked)
                        .forEach(el => (el.checked = true))
                })

        const queryWhereSource = (queryParams.source || []).length
            ? gql`_or: [${queryParams.source
                .map(str => str === 'catapult'
                    ? '{ source_id: { _is_null: true } }'
                    : `{ source: { slug: { _eq: "${str}" } } },`)}]`
            : ''

        const queryWhereTags = (queryParams.topic || []).length
                || (queryParams.media || []).length
            ? gql`_or: [${(queryParams.topic || [])
                .concat(queryParams.media || [])
                .map(str => `{ tags: {slug: { _eq: "${str}" } } },`)}]`
            : ''

        const queryWhereDuration = (durationFilterActive || {}).where
            || ''

        const queryWhere = queryWhereSource || queryWhereTags || queryWhereDuration
            ? gql`where: {
                ${queryWhereSource}${queryWhereSource && queryWhereTags ? ',' : ''}
                ${queryWhereTags}${queryWhereTags && queryWhereDuration ? ',' : ''}
                ${queryWhereDuration}
            },`
            : ''

        const queryArgs = `${isQuery
            ? gql`args: { search: "${queryParams.q}" }`
            : gql`order_by: [{ source_created_at: desc }, { created_at: desc }]`
        }, ${queryWhere
            ? ` ${queryWhere},`
            : ''}`

        const queryName = isQuery
            ? 'articles_search'
            : 'articles'

        const queryAggregate = gql`${queryName}_aggregate ${
            isQuery
                ? `(${queryArgs})`
                : (queryWhere
                    ? `(${queryWhere.replace(/,$/, '')})`
                    : '')}`

        const queryResultsStr = !isLanding
            ? gql`${queryName} (
                ${queryArgs ? `${queryArgs}` : ''}
                limit: ${limit},
                offset: ${offset}
            ) {
                id
                title
                slug
                created_at
                durations
                thumbnail
                tags {
                    id
                    slug
                    title
                }
                source {
                    id
                    slug
                    title
                }
            }`
            : ''

        const query = await graphqlClient
            .request(gql`
                query {
                    ${queryAggregate} {
                        aggregate {
                            count
                        }
                    }

                    ${queryResultsStr}
                }`)
            .catch((err) => {
                console.warn(err)
                return {}
            })

        const queryCount = ((query[`${queryName}_aggregate`] || {})
            .aggregate || {})
            .count || 0

        const queryArticles = query[queryName] || []

        if (!queryArticles)
            console.warn(query)

        if (JSON.stringify(articles)
                !== JSON.stringify(queryArticles)) {
            setArticles({
                articles: queryArticles,
                count: queryCount
            })

            history.replace(`${location.pathname}${location.search}`,
                Object.assign(history.location.state || {}, {
                    articles: queryArticles || null,
                    count: queryCount || null
                }))
        }
    }, [page, queryString])

    useEffect(async () => {
        if (tags === null)
            return

        const querySource = (queryParams || {}).source || []

        const activeSource = querySource.length === 1
            && (sources || [])
                .filter(obj => obj.slug === querySource[0])[0]

        const isActiveSourceChange = activeSource
            && activeSource.id !== (source || {}).id

        const isActiveSourceReset = !activeSource
            && (source || {}).id

        if (isActiveSourceChange)
            setSource(activeSource)

        if (isActiveSourceReset)
            setSource({
                id: null,
                slug: null,
                title: null,
                description: null,
                url: null
            })

        const isQueryChange = `${(location.search || '').replace(/^\?/, '')}`
            !== queryString

        const isPageChange = (page !== null
                && page !== (parseInt(params.page) || 1))
            || isQueryChange

        console.groupCollapsed(`Page change: ${isPageChange}`)
        console.log('Internal Params State:', { page, queryString })
        console.log('Router Params State:', params)
        console.log('Router location.search:', location.search)
        console.groupEnd()

        if (isPageChange) {
            if (comboboxExpanded)
                setComboboxExpanded(false)

            const queryValue = getQueryParams(location.search).q || ''

            if (history.action === 'POP'
                    && (inputSearch.current || {}).value
                        !== queryValue) {
                if (inputSearch.current)
                    inputSearch.current.value = queryValue

                setSearchValue(queryValue)
            }

            const stickyOffset = parseFloat(
                getCssVar('--sticky-nav-offset') || '0')

            const scrollToY = stickyOffset
                    && window.scrollY > stickyOffset
                ? stickyOffset
                : window.scrollY

            await scrollTo(scrollToY)

            setArticles({
                articles: null,
                count: !isQueryChange
                    ? count
                    : null
            })
        }

        setParams({
            page: parseFloat(params.page) || 1,
            queryString: location.search.replace('?', '')
        })

        if (isLanding
                && location.search) {
            Array.from(document
                .querySelectorAll([
                    '.dashboard-hub input[type=checkbox]:checked',
                    '.dashboard-hub input[name=duration][value=all]:not(:checked)'
                ].join(', ')))
                .forEach((el) => el.click())

            history
                .replace(location.pathname, history.location.state)
        }
    }, [params.page, location.pathname, location.search, tags])

    useEffect(async () => {
        const query = await graphqlClient
            .request(gql`
                query {
                    tags (
                        order_by: {
                            slug: asc
                        }
                    ) {
                        id
                        title
                        slug
                    }

                    sources (
                        where: {
                            feed_active: { _eq: true }
                        },
                        order_by: {
                            title: asc
                        }
                    ) {
                        id,
                        title,
                        slug,
                        description,
                        url
                    }
                }
            `)
            .catch((err) => {
                console.warn(err)
                return {}
            })

        let querySources = (query.sources || [])
            .filter(obj => obj.id && obj.title && obj.slug)

        if (querySources.length)
            querySources = [{
                id: -1,
                title: 'Catapult',
                slug: 'catapult'
            }].concat(querySources)

        const isSourcesChange = querySources
            && JSON.stringify(querySources)
                !== JSON.stringify(sources)

        if (isSourcesChange)
            setSources(querySources)

        const queryTags = (query.tags || [])
            .filter(obj => obj.id && obj.title && obj.slug)

        const queryTagsTopics = (queryTags || [])
            .filter(obj => !obj.slug.match(/^(article|audio|video)$/))
            .map(obj => Object.assign(obj, {
                icon: topicIcons[obj.slug]
            }))
            .filter(obj => obj.icon)

        const queryTagsMedia = (queryTags || [])
            .filter(obj => obj.slug.match(/^(article|audio|video)$/))

        const isTagsChange = queryTags
            && JSON.stringify(queryTags)
                !== JSON.stringify(tags)

        if (isTagsChange) {
            setTopics(queryTagsTopics)

            setMediaTypes(queryTagsMedia)

            setTags(queryTags)
        }

        if (isTagsChange || isSourcesChange)
            history.replace(`${location.pathname}${location.search}`,
                Object.assign(history.location.state || {}, {
                    sources: querySources || null,
                    tags: queryTags || null,
                    topics: queryTagsTopics || null,
                    media: queryTagsMedia || null
                }))
    }, [])

    if (tags === null)
        return null

    const isNoResults = !(articles || []).length
        && articles !== null

    const maxPages = !isNoResults
        && count
        && Math.ceil(count / limit)

    let pages = []

    if (maxPages) {
        let start

        const range = 5

        if (page < (range / 2) + 1)
            start = 1

        if (page >= count - (range / 2))
            start = Math.floor(count - range + 1)

        if (!start)
            start = Math.floor(page - (range / 2))

        if (start < 1)
            start = 1

        Array.from(Array(range))
            .forEach((noop, idx) => idx + start <= maxPages
                && pages.push(idx + start))

        if (pages.length < range
                && maxPages > range)
            pages = [pages[0] - 1].concat(pages)
    }

    const onSubmit = async (e) => {
        if (window.innerWidth < 1024
                && filtersExpanded
                && (e.nativeEvent || {}).submitter
                && e.nativeEvent.submitter.classList
                    .contains('dashboard-hub-filters-submit-btn'))
            setFiltersExpanded(false)

        const formData = new window.FormData(e.target)

        const entries = Array.from(formData.entries())

        const queryString = entries
            .filter(arr => Array.isArray(arr)
                && arr[1])
            .map(arr => `${arr[0]}=${arr[1]}`)
            .join('&')

        const searchPathname = Array.isArray(props.searchPath)
                && Boolean(props.searchPath.length)
            ? props.searchPath[0]
            : location.pathname

        history.push(`${searchPathname.replace(/\/page.*$/, '')}${
            queryString
                ? `?${queryString}`
                : ''}`, Object.assign(history.location.state || {}, {
            sources: (history.location.state || {}).sources || sources || null,
            tags: (history.location.state || {}).tags || tags || null,
            media: (history.location.state || {}).media || mediaTypes || null,
            topics: (history.location.state || {}).topics || topics || null
        }))

        const results = document
            .getElementById('dashboard-hub-search-results')

        if (results)
            setTimeout(() => results.focus({ preventScroll: true }), 17)

        return true
    }

    const onStateUpdate = (state) => {
        const { oldValue, newValue } = state || {}

        const isFiltersChange = () => {
            let _oldValue
            let _newValue

            try {
                _oldValue = JSON.parse(oldValue) || {}
                _newValue = JSON.parse(newValue) || {}

                delete _oldValue.q
                delete _newValue.q

                _oldValue.duration = _oldValue.duration || 'all'
                _newValue.duration = _newValue.duration || 'all'

                _oldValue = JSON.stringify(_oldValue)
                _newValue = JSON.stringify(_newValue)
            }
            catch (e) {
                console.warn(e)
            }

            return _oldValue !== _newValue
        }

        const filtersSubmitBtn = form.current
            && form.current.querySelector('.dashboard-hub-filters-submit-btn')

        if (!isFiltersChange()
                && filtersSubmitBtn
                && !filtersSubmitBtn.disabled)
            filtersSubmitBtn.disabled = true
    }

    const onReset = (e) => {
        form.current.classList
            .add('resetting')

        const isResetFilters = e.target.classList
            .contains('dashboard-hub-filters-reset')

        if (isResetFilters)
            Array.from(document
                .querySelectorAll([
                    '.dashboard-hub input[type=checkbox]:checked',
                    '.dashboard-hub input[name=duration][value=all]'
                ].join(', ')))
                .forEach((el) => el.click())

        const isResetQuery = e.target.classList
            .contains('dashboard-hub-input-reset')

        if (isResetQuery
                && (inputSearch.current
                    || {}).value) {
            inputSearch.current.value = ''

            setSearchValue('')
        }

        setTimeout(() => {
            const submitBtn = form.current.querySelector('[type=submit]')

            submitBtn.disabled
                ? onSubmit({ target: form.current })
                : form.current.querySelector('[type=submit]').click()

            form.current.classList
                .remove('resetting')
        }, 34)
    }

    const onComboboxSelect = (item) => {
        const getFilterFromSlug = (filterArr, slug) => filterArr
            .filter(obj => obj.slug === item)[0] || false

        if (!getFilterFromSlug(topics, item))
            return

        const searchPathname = Array.isArray(props.searchPath)
                && Boolean(props.searchPath.length)
            ? props.searchPath[0]
            : location.pathname

        if (searchValue)
            setSearchValue('')

        history.push(`${searchPathname.replace(/\/page.*$/, '')}?topic=${item}`,
            Object.assign(history.location.state || {}, {
                sources: (history.location.state || {}).sources || sources || null,
                tags: (history.location.state || {}).tags || tags || null,
                media: (history.location.state || {}).media || mediaTypes || null,
                topics: (history.location.state || {}).topics || topics || null
            }))

        if (inputSearch.current)
            inputSearch.current
                .blur()

        const results = document
            .getElementById('dashboard-hub-search-results')

        if (results)
            setTimeout(() => results.focus({ preventScroll: true }), 17)

        const filtersSubmitBtn = form.current
            && form.current.querySelector('.dashboard-hub-filters-submit-btn')

        if (filtersSubmitBtn)
            setTimeout(() => (filtersSubmitBtn.disabled = true), 17)
    }

    const basePathname = (location.pathname || window.location.pathname)
        .replace(/\/page.+/, '')
        .replace(/\/$/, '')

    const rootPathname = basePathname
        .replace(/\/(search).*/, '')
        .replace(/\/$/, '')

    return (
        <div
            className="dashboard-hub pad-3-t pad-4-b pad-4-t-lg-up"
            data-landing={isLanding}
        >
            <div className="container">
                <div className="dashboard-hub-header row-condensed flex-justify-content-space-between margin-b margin-3-b-lg-up">
                    {!isLanding
                        && (
                            <div className="col-12">
                                <Link
                                    to={rootPathname}
                                    className="body-4 display-inline-flex"
                                    style={{
                                        color: getCssVar('--slate'),
                                        marginBottom: '5px',
                                        lineHeight: 1.1,
                                        overflow: 'hidden'
                                    }}
                                    onClick={() => {
                                        if ((inputSearch.current || {}).value) {
                                            inputSearch.current.value = ''

                                            setSearchValue('')
                                        }
                                    }}
                                >
                                    <svg
                                        className="icon-chevron-left flex-none"
                                        width="18"
                                        height="18"
                                        style={{ margin: '-2px 2px 0 -5px' }}
                                    >
                                        <use xlinkHref="#icon-chevron-left" />
                                    </svg>
                                    <span>Go back</span>
                                </Link>
                            </div>)}

                    <div className="col-12 col-7-lg-up">
                        <h1 className="heading-2 display-flex">
                            <span className="display-inline-block">
                                {(source || {}).title === 'Catapult'
                                    ? `${source.title} exclusive`
                                    : (source || {}).title || 'Content hub'}
                            </span>
                            {(source || {}).slug === 'catapult'
                                && (
                                    <span
                                        className="display-inline-flex"
                                        style={{ marginLeft: 6 }}
                                    >
                                        <span
                                            className="ratio-square round flex-justify-content-center"
                                            style={{
                                                width: 22,
                                                height: 22,
                                                background: getCssVar('--aqua'),
                                                color: getCssVar('--navy'),
                                                marginTop: 8
                                            }}
                                        >
                                            <svg
                                                width="11"
                                                style={{ marginLeft: -1 }}
                                            >
                                                <use xlinkHref="#logo-catapult-icon" />
                                            </svg>
                                        </span>
                                    </span>
                                )}
                        </h1>
                        {Boolean(source.id || props.richText)
                            && (
                                <RichText
                                    className="pad-1-t body-3"
                                    html={source.id
                                        ? source.description
                                        : props.richText}
                                />)}
                        {Boolean(source.url)
                            && (
                                <div className="pad-1-t">
                                    <a
                                        className="body-3 text-decoration-underline"
                                        href={source.url}
                                        rel="noopener noreferrer"
                                        target="_blank"
                                        title="Opens in new tab"
                                    >
                                        <span
                                            style={{ marginRight: 3 }}
                                        >
                                            Explore more
                                        </span>
                                        <svg
                                            width="18"
                                            height="18"
                                        >
                                            <use xlinkHref="#icon-launch" />
                                        </svg>
                                    </a>
                                </div>
                            )}
                    </div>
                </div>
                <div
                    className="row-condensed"
                    style={{ position: 'static' }}
                >
                    <div
                        className="col-12 col-3-lg-up"
                        style={{ position: 'static' }}
                    >
                        <Form
                            formRef={(ref) => (form.current = ref)}
                            id="dashboard-hub-search"
                            tabIndex="-1"
                            enableOnChange
                            enableOnChangeSyncHistoryPop
                            enableOnChangeSyncHistoryPush
                            onSubmit={onSubmit}
                            onStateUpdate={onStateUpdate}
                            role="search"
                            aria-label="Search"
                            style={{ position: 'static' }}
                        >
                            <div
                                ref={combobox}
                                className="dashboard-hub-combobox col-4-lg-up pad-0-lg-down margin-b-lg-down"
                                data-expanded={isComboboxExpandable
                                    ? `${Boolean(comboboxExpanded)}`
                                    : null}
                            >
                                <div
                                    className="dashboard-hub-combobox-wrapper"
                                    tabIndex="-1"
                                    aria-labelledby={isComboboxExpandable
                                            && comboboxExpanded
                                        ? 'dashboard-hub-combobox-field'
                                        : null}
                                    aria-modal={isComboboxExpandable
                                        ? `${Boolean(comboboxExpanded)}`
                                        : null}
                                    role={comboboxExpanded
                                        ? 'dialog'
                                        : null}
                                >
                                    <div className="dashboard-hub-combobox-container">
                                        <div
                                            className={`margin-b${!comboboxExpanded
                                                ? ' display-none'
                                                : ''}`}
                                        >
                                            <button
                                                className="dashboard-hub-combobox-modal-close-btn btn-unstyled"
                                                onClick={() =>
                                                    setComboboxExpanded(false)}
                                                aria-controls="dashboard-hub-combobox-modal"
                                                aria-expanded="true"
                                                aria-label="Hide search modal"
                                            >
                                                <svg
                                                    className="icon-close"
                                                    width="18"
                                                    height="18"
                                                >
                                                    <use xlinkHref="#icon-close" />
                                                </svg>
                                            </button>
                                        </div>
                                        <div className="form-field">
                                            <Combobox
                                                id="dashboard-hub-combobox-field"
                                                aria-label="Search the Content hub"
                                                onSelect={onComboboxSelect}
                                                openOnFocus
                                            >
                                                <div>
                                                    <ComboboxInput
                                                        ref={inputSearch}
                                                        id="dashboard-hub-combobox-input"
                                                        name="q"
                                                        autocomplete={false}
                                                        autoComplete="off"
                                                        placeholder={`Search${
                                                            size.width > 374
                                                                ? ' by title or keyword'
                                                                : ''}`}
                                                        onChange={(e) => e.target.value !== searchValue
                                                            && setSearchValue(e.target.value)}
                                                        onKeyUp={(e) => e.key
                                                            && (e.key.length === 1
                                                                || e.key === 'Backspace')
                                                            && setSearchValue(e.target.value)}
                                                        onKeyDown={(e) => e.key
                                                            && (e.key.length === 1
                                                                || e.key === 'Backspace')
                                                            && isComboboxExpandable
                                                            && !comboboxExpanded
                                                            && setComboboxExpanded(true)}
                                                        onBlur={(e) =>
                                                            setSearchValue(e.target.value)}
                                                        onClick={(e) => isComboboxExpandable
                                                            && !comboboxExpanded
                                                            && setComboboxExpanded(true)}
                                                        value={searchValue}
                                                    />

                                                    <button
                                                        className="btn-unstyled dashboard-hub-input-clear"
                                                        type="button"
                                                        aria-label="Clear search text"
                                                        onClick={(e) => {
                                                            setSearchValue('')

                                                            setTimeout(() => {
                                                                inputSearch.current
                                                                    && inputSearch.current
                                                                        .focus()
                                                            }, 34)
                                                        }}
                                                        disabled={!searchValue}
                                                    >
                                                        <svg
                                                            className="flex-none icon-close"
                                                            width="18"
                                                            height="18"
                                                        >
                                                            <use xlinkHref="#icon-close" />
                                                        </svg>
                                                    </button>

                                                    <button
                                                        className="btn-unstyled dashboard-hub-submit"
                                                        type="submit"
                                                        aria-label="Submit"
                                                        disabled
                                                    >
                                                        <svg
                                                            className="flex-none icon-search"
                                                            width="24"
                                                            height="24"
                                                        >
                                                            <use xlinkHref="#icon-search" />
                                                        </svg>
                                                    </button>
                                                </div>
                                                {(() => {
                                                    const listItems = (topics || [])
                                                        .filter((obj) => !searchValue
                                                            || (obj.title || '')
                                                                .replace(/\W/g, ' ')
                                                                .split(/\s+/)
                                                                .map(str => str
                                                                    .trim()
                                                                    .toLowerCase())
                                                                .some(str => str
                                                                    .match(new RegExp(
                                                                        `^${searchValue}`,
                                                                        'i'))))

                                                    return (
                                                        <ComboboxPopover
                                                            portal={false}
                                                            data-empty={!listItems.length}
                                                        >
                                                            <ComboboxList>
                                                                {listItems
                                                                    .map((obj, idx) => (
                                                                        <ComboboxOption
                                                                            key={`dashboard-hub-combobox-field-list-item-${obj.slug}`}
                                                                            value={obj.slug}
                                                                        >
                                                                            <div className="display-flex flex-nowrap">
                                                                                <svg
                                                                                    width="18"
                                                                                    height="18"
                                                                                    style={{ marginRight: 5 }}
                                                                                >
                                                                                    <use xlinkHref={`#${obj.icon}`} />
                                                                                </svg>
                                                                                <span>{obj.title}</span>
                                                                            </div>
                                                                        </ComboboxOption>
                                                                    ))}
                                                            </ComboboxList>
                                                        </ComboboxPopover>
                                                    )
                                                })()}
                                            </Combobox>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="display-none-lg-up">
                                <DashboardHubTopicsCard
                                    topics={topics}
                                    searchPathname={(props.searchPath || [])[0]
                                        || rootPathname}
                                    expandable
                                />
                            </div>
                            <div
                                className={`card margin-b-lg-down pad-0-t-lg-down pad-0-b-lg-down pad-3-l pad-3-r${
                                    isLanding
                                        ? ' display-none'
                                        : ''}`}
                                role="search"
                                aria-label="Search filters"
                            >
                                <a
                                    className="skip-to margin-b body-4 display-none-lg-down"
                                    href="#dashboard-hub-search-results"
                                >
                                    Skip search filters
                                </a>
                                <button
                                    className="btn-unstyled dashboard-hub-controls-toggle display-none-lg-up text-decoration-hover"
                                    type="button"
                                    aria-controls="dashboard-hub-controls"
                                    aria-expanded={filtersExpanded}
                                    aria-label={`${filtersExpanded ? 'Hide' : 'Show'} filters`}
                                    aria-describedby={getQueryParamsCount()
                                        && 'dashboard-hub-controls-toggle-filter-count'}
                                    onClick={(e) => setFiltersExpanded(!filtersExpanded)}
                                >
                                    <span className="body-3 display font-weight-medium">Filters</span>
                                    {Boolean(getQueryParamsCount())
                                        && (
                                            <span
                                                id="dashboard-hub-controls-toggle-filter-count"
                                                aria-label={`${
                                                    getQueryParamsCount()} active filter${
                                                    getQueryParamsCount() > 1
                                                        ? 's'
                                                        : ''}`}
                                                className="ratio-sqaure round margin-1-l"
                                                style={{
                                                    backgroundColor: getCssVar('--aqua'),
                                                    color: getCssVar('--navy'),
                                                    width: 20,
                                                    height: 20,
                                                    fontSize: 12,
                                                    fontWeight: 400,
                                                    lineHeight: '20px',
                                                    marginRight: 'auto',
                                                    textDecoration: 'none !important'
                                                }}
                                            >
                                                {getQueryParamsCount()}
                                            </span>)}
                                </button>
                                <div
                                    id="dashboard-hub-controls"
                                    className="dashboard-hub-controls pad-b-lg-down"
                                >
                                    <h2 className="body-3 font-weight-medium display display-none-lg-down">
                                        Filters
                                    </h2>
                                    {Boolean((topics || []).length)
                                        && (
                                            <fieldset className="form-field">
                                                <div>
                                                    <legend
                                                        className="body-4 display"
                                                        aria-label="Filter by topic"
                                                    >
                                                        Topic
                                                    </legend>
                                                    <button
                                                        className="btn-unstyled dashboard-hub-controls-filter-toggle"
                                                        type="button"
                                                        aria-controls="dashboard-hub-controls-filter-0"
                                                        aria-expanded={topicFiltersExpanded}
                                                        aria-label={`${topicFiltersExpanded ? 'Hide' : 'Show'} topic filters`}
                                                        onClick={(e) => setTopicFiltersExpanded(!topicFiltersExpanded)}
                                                    />
                                                </div>
                                                <div
                                                    id="dashboard-hub-controls-filter-0"
                                                    className={!topicFiltersExpanded
                                                        ? 'display-none'
                                                        : null}
                                                >
                                                    {topics
                                                        .map((obj, idx) => (
                                                            <div
                                                                className="display-flex margin-1-t"
                                                                key={`dashboard-hub-topic-${idx}`}
                                                            >
                                                                <input
                                                                    id={`dashboard-hub-topic-${idx}`}
                                                                    type="checkbox"
                                                                    value={obj.slug}
                                                                    name="topic"
                                                                    defaultChecked={
                                                                        (queryParams
                                                                            .topic || [])
                                                                            .indexOf(
                                                                                obj.slug)
                                                                                    >= 0
                                                                    }
                                                                />
                                                                <label
                                                                    htmlFor={`dashboard-hub-topic-${idx}`}
                                                                    className="body-5"
                                                                    style={{
                                                                        marginTop: 3
                                                                    }}
                                                                >
                                                                    <span className="font-weight-normal">{obj.title}</span>
                                                                </label>
                                                            </div>
                                                        ))}
                                                </div>
                                            </fieldset>)}
                                    {Boolean((mediaTypes || []).length)
                                        && (
                                            <fieldset className="form-field">
                                                <div>
                                                    <legend
                                                        className="body-4 display"
                                                        aria-label="Filter by media type"
                                                    >
                                                        Media type
                                                    </legend>
                                                    <button
                                                        className="btn-unstyled dashboard-hub-controls-filter-toggle"
                                                        type="button"
                                                        aria-controls="dashboard-hub-controls-filter-1"
                                                        aria-expanded={mediaFiltersExpanded}
                                                        aria-label={`${mediaFiltersExpanded ? 'Hide' : 'Show'} media type filters`}
                                                        onClick={(e) => setMediaFiltersExpanded(!mediaFiltersExpanded)}
                                                    />
                                                </div>
                                                <div
                                                    id="dashboard-hub-controls-filter-1"
                                                    className={!mediaFiltersExpanded
                                                        ? 'display-none'
                                                        : null}
                                                >
                                                    {mediaTypes
                                                        .map((obj, idx) => (
                                                            <div
                                                                className="display-flex margin-1-t"
                                                                key={`dashboard-hub-media-${idx}`}
                                                            >
                                                                <input
                                                                    id={`dashboard-hub-media-${idx}`}
                                                                    type="checkbox"
                                                                    value={obj.slug}
                                                                    name="media"
                                                                    defaultChecked={
                                                                        (queryParams
                                                                            .media || [])
                                                                            .indexOf(
                                                                                obj.slug)
                                                                                    >= 0
                                                                    }
                                                                />
                                                                <label
                                                                    htmlFor={`dashboard-hub-media-${idx}`}
                                                                    className="body-5"
                                                                    style={{
                                                                        marginTop: 3
                                                                    }}
                                                                >
                                                                    <span className="font-weight-normal">{obj.title}</span>
                                                                </label>
                                                            </div>
                                                        ))}
                                                </div>
                                            </fieldset>)}
                                    <fieldset className="form-field">
                                        <div>
                                            <legend
                                                className="body-4 display"
                                                aria-label="Filter by duration"
                                            >
                                                Duration
                                            </legend>
                                            <button
                                                className="btn-unstyled dashboard-hub-controls-filter-toggle"
                                                type="button"
                                                aria-controls="dashboard-hub-controls-filter-2"
                                                aria-expanded={durationFiltersExpanded}
                                                aria-label={`${durationFiltersExpanded ? 'Hide' : 'Show'} durations filters`}
                                                onClick={(e) => setDurationFiltersExpanded(!durationFiltersExpanded)}
                                            />
                                        </div>
                                        <div
                                            id="dashboard-hub-controls-filter-2"
                                            className={!durationFiltersExpanded
                                                ? 'display-none'
                                                : null}
                                        >
                                            {durationFilters
                                                .map((obj, idx) => (
                                                    <div
                                                        className="display-flex margin-1-t"
                                                        key={`dashboard-hub-duration-${obj.value}`}
                                                    >
                                                        <input
                                                            id={`dashboard-hub-duration-${obj.value}`}
                                                            type="radio"
                                                            value={obj.value}
                                                            name="duration"
                                                            defaultChecked={(durationFilterActive
                                                                || {}).value === obj.value}
                                                        />
                                                        <label
                                                            htmlFor={`dashboard-hub-duration-${obj.value}`}
                                                            className="body-5"
                                                            style={{
                                                                marginTop: 3
                                                            }}
                                                        >
                                                            <span className="font-weight-normal">{obj.label}</span>
                                                        </label>
                                                    </div>
                                                ))}
                                        </div>
                                    </fieldset>
                                    {Boolean((sources || []).length)
                                        && (
                                            <fieldset className="form-field">
                                                <div>
                                                    <legend
                                                        className="body-4 display"
                                                        aria-label="Filter by source"
                                                    >
                                                        Source
                                                    </legend>
                                                    <button
                                                        className="btn-unstyled dashboard-hub-controls-filter-toggle"
                                                        type="button"
                                                        aria-controls="dashboard-hub-controls-filter-3"
                                                        aria-expanded={sourceFiltersExpanded}
                                                        aria-label={`${sourceFiltersExpanded ? 'Hide' : 'Show'} source filters`}
                                                        onClick={(e) => setSourceFiltersExpanded(!sourceFiltersExpanded)}
                                                    />
                                                </div>
                                                <div
                                                    id="dashboard-hub-controls-filter-3"
                                                    className={!sourceFiltersExpanded
                                                        ? 'display-none'
                                                        : null}
                                                >
                                                    {sources
                                                        .map((obj, idx) => (
                                                            <div
                                                                className="display-flex margin-1-t"
                                                                key={`dashboard-hub-source-${idx}`}
                                                            >
                                                                <input
                                                                    id={`dashboard-hub-source-${idx}`}
                                                                    type="checkbox"
                                                                    value={obj.slug}
                                                                    name="source"
                                                                    defaultChecked={
                                                                        (queryParams
                                                                            .source || [])
                                                                            .indexOf(
                                                                                obj.slug)
                                                                                    >= 0
                                                                    }
                                                                />
                                                                <label
                                                                    htmlFor={`dashboard-hub-source-${idx}`}
                                                                    className="body-5"
                                                                    style={{
                                                                        marginTop: 3
                                                                    }}
                                                                >
                                                                    <span className="font-weight-normal">{obj.title}</span>
                                                                </label>
                                                            </div>
                                                        ))}
                                                </div>
                                            </fieldset>)}
                                    <div className="pad-3-t">
                                        <div className="row-condensed flex-align-items-center">
                                            <div className="col-auto pad-0-r">
                                                <button
                                                    className="dashboard-hub-filters-submit-btn btn-secondary pad-1"
                                                    type="submit"
                                                    disabled
                                                >
                                                    <span
                                                        className="body-5 font-weight-medium"
                                                        style={{
                                                            lineHeight: 1
                                                        }}
                                                    >
                                                        Apply Filters
                                                    </span>
                                                </button>
                                            </div>
                                            <div className="col-auto">
                                                <button
                                                    className="btn-unstyled dashboard-hub-filters-reset"
                                                    disabled={!(Boolean(queryParams.topic)
                                                            || Boolean(queryParams.media)
                                                            || Boolean(queryParams.source)
                                                            || queryParams.duration !== 'all')
                                                        || isLanding
                                                        || (articles === null
                                                            && count === null)}
                                                    type="button"
                                                    onClick={onReset}
                                                >
                                                    <span
                                                        className="body-5 font-weight-book"
                                                        style={{
                                                            lineHeight: 1
                                                        }}
                                                    >
                                                        Clear all
                                                    </span>
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </Form>
                    </div>
                    <div
                        className={`col${
                            isLanding
                                ? ' display-none'
                                : ''}`}
                    >
                        <section
                            id="dashboard-hub-search-results"
                            className="dashboard-hub-results card card-offscreen-x-lg-down pad-3-t-lg-up"
                            aria-label="Search results"
                            tabIndex="-1"
                        >
                            {!isLanding
                                && (
                                    <>
                                        <div className="row-condensed body-5 font-weight-book flex-align-items-center flex-justify-content-end margin-b">
                                            {Boolean(queryParams.q)
                                                && (
                                                    <div className="col body-3 display-inline-flex">
                                                        <p>
                                                            <span>
                                                                {count === null
                                                                    ? 'Searching'
                                                                    : `${isNoResults
                                                                        ? 'No r'
                                                                        : 'R'}esults`} for&nbsp;
                                                            </span>
                                                            <em
                                                                style={{
                                                                    color: getCssVar('--navy'),
                                                                    fontWeight: 500
                                                                }}
                                                            >
                                                                {queryParams.q}
                                                            </em>
                                                        </p>
                                                        <button
                                                            className="btn-unstyled dashboard-hub-input-reset margin-2-l text-decoration-underline"
                                                            type="button"
                                                            onClick={onReset}
                                                            aria-label="Clear search query"
                                                        >
                                                            <span className="body-5 font-weight-normal">Clear</span>
                                                        </button>
                                                    </div>)}
                                            <div
                                                className="col-auto"
                                                role="status"
                                            >
                                                <p
                                                    style={{
                                                        color: getCssVar('--slate'),
                                                        visibility: articles === null
                                                                || count === null
                                                            ? ' hidden'
                                                            : null
                                                    }}
                                                >
                                                    {count} result{count !== 1 ? 's' : ''}
                                                </p>
                                            </div>
                                        </div>
                                        {!isNoResults
                                            && articles !== null
                                            && (
                                                <ul className="row-condensed">
                                                    {(articles || [])
                                                        .map((obj, idx) => (
                                                            <li
                                                                className="col-12 col-6-md col-4-lg-up margin-3-b margin-4-b-lg-down display-flex"
                                                                key={`dashboard-home-article-${idx}}`}
                                                            >
                                                                <ArticleCard
                                                                    idPrefix={`article-card-${idx}`}
                                                                    {...obj}
                                                                />
                                                            </li>
                                                        ))}
                                                </ul>)}
                                        {isNoResults
                                            && (
                                                <div
                                                    className="dashboard-hub-no-results card-secondary body-4 text-align-center"
                                                    role="status"
                                                >
                                                    <h2 className="heading-6">No results</h2>
                                                    <p>Please adjust your search.</p>
                                                </div>
                                            )}
                                        {articles === null
                                            && (
                                                <div className="dashboard-hub-pending-results display-flex flex-align-items-center flex-justify-content-center">
                                                    <LoadingIndicator />
                                                </div>)}
                                    </>)}
                        </section>
                        {Boolean(pages.length)
                            && !isLanding
                            && (
                                <nav
                                    className="pagination container-flex flex-justify-content-center margin-3-t margin-5-t-lg-up"
                                    aria-label="Search results pages"
                                    style={{
                                        visibility: (articles || []).length
                                            ? 'visible'
                                            : 'hidden'
                                    }}
                                >
                                    <Link
                                        to={`${basePathname}${
                                            page > 2
                                                ? `/page/${page - 1}`
                                                : ''}${
                                            location.search}`}
                                        className="pagination-prev flex-none"
                                        aria-label="Go to previous page"
                                        aria-disabled={page === 1}
                                    >
                                        <svg
                                            className="flex-none icon-chevron-left"
                                            width="24"
                                            height="24"
                                            style={{ maxHeight: '24px' }}
                                        >
                                            <use xlinkHref="#icon-chevron-left" />
                                        </svg>
                                    </Link>
                                    <ul className="display-flex flex-wrap">
                                        {pages
                                            .map((idx) => {
                                                return (
                                                    <li
                                                        key={`dashboard-hub-pagination-${idx}`}
                                                        className="flex-none"
                                                    >
                                                        <Link
                                                            to={`${basePathname}${
                                                                idx > 1
                                                                    ? `/page/${idx}`
                                                                    : ''}${
                                                                location.search}`}
                                                            className="text-decoration-hover"
                                                            aria-label={`Page ${idx}`}
                                                            aria-current={page === idx
                                                                && 'page'}
                                                        >
                                                            <span>{idx}</span>
                                                        </Link>
                                                    </li>
                                                )
                                            })}
                                    </ul>
                                    <Link
                                        to={`${basePathname}${
                                            page < maxPages
                                                ? `/page/${page + 1}`
                                                : ''}${
                                            location.search}`}
                                        className="pagination-next flex-none"
                                        role="link"
                                        aria-label="Go to next page"
                                        aria-disabled={page === maxPages}
                                    >
                                        <svg
                                            className="flex-none icon-chevron-right"
                                            width="24"
                                            height="24"
                                            style={{ maxHeight: '24px' }}
                                        >
                                            <use xlinkHref="#icon-chevron-right" />
                                        </svg>
                                    </Link>
                                </nav>)}
                    </div>
                    {isLanding
                        && (
                            <div className="col-12 pad-3-t-lg-up">
                                <DashboardHubLanding
                                    topics={topics}
                                    sources={sources}
                                    searchPathname={(props.searchPath || [])[0]
                                        || rootPathname}
                                />
                            </div>
                        )}
                </div>
            </div>
        </div>
    )
}